// ─────────────────────────────────────────────────────────────────────────────
// Build save format — minimal & versioned.
//
// A "build" is stored as a compact payload that keeps only the identifying data
// (item ids, card/enchant ids, refine, options, stats, job, levels, skills).
// Everything else (scripts, names, item stats) is re-hydrated from the live DB
// on load, exactly like the legacy inventory rehydration already did by id.
//
// Cards are stored as ids only; their Script/AegisName/Type are looked up in the
// card / enchant DB at load time (see deserializeBuild).
// ─────────────────────────────────────────────────────────────────────────────

export const BUILD_VERSION = 1

// 17 equipment locations, fixed order. Mirrors the clean inventory in the
// character context.
export const LOCATIONS = [
	'Head_Top', 'Head_Mid', 'Head_Low', 'Armor',
	'Right_Hand', 'Left_Hand', 'Garment', 'Shoes',
	'Right_Accessory', 'Left_Accessory',
	'Shadow_Weapon', 'Shadow_Shield', 'Shadow_Armor', 'Shadow_Shoes',
	'Shadow_Right_Accessory', 'Shadow_Left_Accessory',
	'Ammo',
]

const CARD_SLOTS  = 4
const OPTION_SLOTS = 15

// ─── Serialize ────────────────────────────────────────────────────────────────

/**
 * Build a minimal payload from the live character state.
 * @param {object} player  result of refreshPlayer() — a Character instance
 *                         ({ Stats, Job, BaseLevel, JobLevel, Inventory })
 * @param {Array}  skills  the skills array ([{ AegisName, skillLevel }])
 * @returns {object} versioned payload (without name/updatedAt — those are slot metadata)
 */
export function serializeBuild(player, skills = [], buffs = {}) {
	const stats = player?.Stats ?? {}
	const payload = {
		v: BUILD_VERSION,
		stats: {
			str: num(stats.str, 1),
			agi: num(stats.agi, 1),
			vit: num(stats.vit, 1),
			int: num(stats.int, 1),
			dex: num(stats.dex, 1),
			luk: num(stats.luk, 1),
			// 4th job stats reserved for a future UI — always 0 for now.
			pow: num(stats.pow, 0),
			sta: num(stats.sta, 0),
			wis: num(stats.wis, 0),
			spl: num(stats.spl, 0),
			con: num(stats.con, 0),
			crt: num(stats.crt, 0),
		},
		job: num(player?.Job, 0),
		baseLevel: num(player?.BaseLevel, 1),
		jobLevel: num(player?.JobLevel, 1),
		inventory: serializeInventory(player?.Inventory ?? {}),
	}

	const sk = serializeSkills(skills)
	if (sk.length) payload.skills = sk

	const bf = serializeBuffs(buffs)
	if (Object.keys(bf).length) payload.buffs = bf

	return payload
}

// Active buffs → { [skillAegis]: level } for level > 0 only.
function serializeBuffs(buffs) {
	const out = {}
	if (buffs && typeof buffs === 'object') {
		for (const [skill, level] of Object.entries(buffs)) {
			const lv = num(level, 0)
			if (lv > 0) out[skill] = lv
		}
	}
	return out
}

function serializeInventory(inventory) {
	const out = {}
	for (const loc of LOCATIONS) {
		const item = inventory[loc]
		if (!item || !item.Id) continue // empty slot → omitted

		const entry = { id: num(item.Id, 0) }

		const refine = num(item.refine, 0)
		if (refine) entry.refine = refine

		const cards = serializeCards(item.CardSlots)
		if (cards) entry.cards = cards

		const opts = serializeOptions(item.Options)
		if (opts.length) entry.opts = opts

		out[loc] = entry
	}
	return out
}

// Cards → array of 4 ids, trailing zeros trimmed. Returns null if all empty.
function serializeCards(cardSlots) {
	if (!Array.isArray(cardSlots)) return null
	const ids = []
	for (let i = 0; i < CARD_SLOTS; i++) {
		const slot = cardSlots[i]
		// Slot uses .ID, legacy/DB sometimes .Id
		ids.push(num(slot?.ID ?? slot?.Id, 0))
	}
	while (ids.length && ids[ids.length - 1] === 0) ids.pop()
	return ids.length ? ids : null
}

// Options → sparse [{ i, id, v }] for non-empty option slots only.
function serializeOptions(options) {
	if (!Array.isArray(options)) return []
	const out = []
	for (let i = 0; i < OPTION_SLOTS; i++) {
		const opt = options[i]
		if (opt && opt.optionId !== '' && opt.optionId != null) {
			out.push({ i, id: opt.optionId, v: num(opt.value, 0) })
		}
	}
	return out
}

function serializeSkills(skills) {
	if (!Array.isArray(skills)) return []
	return skills
		.filter((s) => s && s.AegisName && num(s.skillLevel, 0) > 0)
		.map((s) => ({ a: s.AegisName, lv: num(s.skillLevel, 0) }))
}

// ─── Deserialize ──────────────────────────────────────────────────────────────

/**
 * Expand a payload back into the shapes the character context consumes.
 * The returned tempInventory keeps cards as ids; the context rehydrates them
 * from the card / enchant DB (gated by isDbRefreshed), like items.
 *
 * @returns {{ stats, job, baseLevel, jobLevel, tempInventory, tempSkills }}
 */
export function deserializeBuild(payload) {
	const p = payload ?? {}
	const stats = p.stats ?? {}

	return {
		stats: {
			str: num(stats.str, 1),
			agi: num(stats.agi, 1),
			vit: num(stats.vit, 1),
			int: num(stats.int, 1),
			dex: num(stats.dex, 1),
			luk: num(stats.luk, 1),
		},
		job: num(p.job, 0),
		baseLevel: num(p.baseLevel, 1),
		jobLevel: num(p.jobLevel, 1),
		tempInventory: deserializeInventory(p.inventory ?? {}),
		tempSkills: (p.skills ?? []).map((s) => ({
			AegisName: s.a,
			skillLevel: num(s.lv, 0),
		})),
		buffs: deserializeBuffs(p.buffs),
	}
}

function deserializeBuffs(buffs) {
	const out = {}
	if (buffs && typeof buffs === 'object') {
		for (const [skill, level] of Object.entries(buffs)) {
			out[skill] = num(level, 0)
		}
	}
	return out
}

function deserializeInventory(inventory) {
	const out = {}
	for (const loc of LOCATIONS) {
		const entry = inventory[loc]
		if (!entry || !entry.id) continue

		out[loc] = {
			Id: num(entry.id, 0),
			refine: num(entry.refine, 0),
			cards: padCards(entry.cards),
			Options: expandOptions(entry.opts),
		}
	}
	return out
}

// Always return a 4-length id array.
function padCards(cards) {
	const out = [0, 0, 0, 0]
	if (Array.isArray(cards)) {
		for (let i = 0; i < CARD_SLOTS; i++) out[i] = num(cards[i], 0)
	}
	return out
}

// Rebuild the dense 15-length options array from the sparse form.
function expandOptions(opts) {
	const out = Array.from({ length: OPTION_SLOTS }, () => ({ optionId: '', value: 0 }))
	if (Array.isArray(opts)) {
		for (const o of opts) {
			const i = num(o?.i, -1)
			if (i >= 0 && i < OPTION_SLOTS) {
				out[i] = { optionId: o.id ?? '', value: num(o.v, 0) }
			}
		}
	}
	return out
}

// ─── Legacy migration ─────────────────────────────────────────────────────────

/**
 * True if a stored slot is already in the versioned (v1+) format.
 */
export function isVersioned(slot) {
	return slot && typeof slot === 'object' && typeof slot.v === 'number'
}

/**
 * Convert a legacy localStorage slot (raw Character JSON, no `v`, no name) into
 * a v1 payload. Legacy empty slots used Job === 'Empty'.
 */
export function migrateLegacySlot(legacy) {
	if (!legacy || typeof legacy !== 'object') return serializeBuild({})

	const stats = legacy.Stats ?? {}
	const job = legacy.Job === 'Empty' || legacy.Job == null ? 0 : num(legacy.Job, 0)

	const payload = {
		v: BUILD_VERSION,
		stats: {
			str: num(stats.str, 1),
			agi: num(stats.agi, 1),
			vit: num(stats.vit, 1),
			int: num(stats.int, 1),
			dex: num(stats.dex, 1),
			luk: num(stats.luk, 1),
			pow: 0, sta: 0, wis: 0, spl: 0, con: 0, crt: 0,
		},
		job,
		baseLevel: num(legacy.BaseLevel, 1),
		jobLevel: num(legacy.JobLevel, 1),
		inventory: migrateLegacyInventory(legacy.Inventory ?? {}),
	}
	return payload
}

function migrateLegacyInventory(inventory) {
	const out = {}
	for (const loc of LOCATIONS) {
		const item = inventory[loc]
		if (!item || !item.Id) continue

		const entry = { id: num(item.Id, 0) }

		const refine = num(item.refine, 0)
		if (refine) entry.refine = refine

		const cards = serializeCards(item.CardSlots)
		if (cards) entry.cards = cards

		const opts = serializeOptions(item.Options)
		if (opts.length) entry.opts = opts

		out[loc] = entry
	}
	return out
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function num(value, fallback) {
	const n = Number(value)
	return Number.isFinite(n) ? n : fallback
}
